/* import __COLOCATED_TEMPLATE__ from './prism-tin-input.hbs'; */
import Component from '@ember/component';
import { isPresent } from '@ember/utils';
import { computed } from '@ember/object';
// eslint-disable-next-line import/default
import FieldKit from 'field-kit';
import { TIN_LAST_FOUR_REGEX } from '@square/ember-prism/utils/regexes';
import { maskTin, maskTinLast4, normalizeTin } from './prism/input-tin';

/**
 * This component renders a text input with TIN/SSN formatting. It will
 * automatically mask & unmask the value on blur & focus.
 *
 * For an unformatted TIN:
 * ```
 * {{prism-tin-input
 *   value=nameOfValueProperty
 *   inputId="my-id"
 *   inputClass="some-class some-other-class"
 *   placeholder="000000000"
 *   onChange=(action (mut nameOfValueProperty))}}
 * ```
 *
 * For a SSN:
 * ```
 * {{prism-tin-input
 *   value=nameOfValueProperty
 *   inputId="my-id"
 *   inputClass="some-class some-other-class"
 *   isSsn=true
 *   placeholder="000-00-0000"
 *   onChange=(action (mut nameOfValueProperty))}}
 * ```
 * For an EIN:
 * ```
 * {{prism-tin-input
 *   value=nameOfValueProperty
 *   inputId="my-id"
 *   inputClass="some-class some-other-class"
 *   isEin=true
 *   placeholder="00-0000000"
 *   onChange=(action (mut nameOfValueProperty))}}
 * ```
 *
 * @class PrismTinInput
 */
export default Component.extend({
  /**
   * ID to apply to the underlying input field.
   *
   * @argument inputId
   * @type {String}
   */
  inputId: null,
  /**
   * Class to apply to the underlying input field.
   *
   * @argument inputClass
   * @type {String}
   */
  inputClass: null,
  /**
   * Placeholder of the underlying input field.
   *
   * @argument placeholder
   * @type {String}
   */
  placeholder: null,
  /**
   * The initial TIN value.
   *
   * @argument value
   * @type {String}
   */
  value: null,
  /**
   * An action that is called when the field is changed. The action is called
   * with the fully normalized TIN. **Required**.
   *
   * @argument onChange
   * @type {Action}
   */
  onChange() {},
  /**
   * Enables SSN formatting (XXX-XX-XXXX).
   *
   * @argument isSsn
   * @type {Boolean}
   */
  isSsn: false,
  /**
   * Enables EIN formatting (XX-XXXXXXX).
   *
   * @argument isEin
   * @type {Boolean}
   */
  isEin: false,
  /**
   * Optional passed last 4 digits of a TIN. If this is passed the initial
   * value of the input will be the last 4 when not focused and empty when
   * focused.
   *
   * Touching the field will clear the last4.
   *
   * @argument prefilledLast4
   * @type {String}
   */
  prefilledLast4: null,
  /**
   * Optional attribute to determine whether the field should have autocomplete
   * enabled. Note that autocomplete does not play nicely with masking -- some
   * browsers attempt to auto fill the field with the •••• characters.
   *
   * @argument autocomplete
   * @type {String}
   */
  autocomplete: 'off',
  disabled: false,
  maxLength: computed('isSsn', 'isEin', function () {
    if (this.isSsn) {
      return '11';
    } else if (this.isEin) {
      return '10';
    } else {
      return '9';
    }
  }),
  init(...args) {
    this._super(...args);
    this.set('_internalValue', this.value);
    this._unsetInvalidLast4();
    this._mask();
  },
  focusIn() {
    this._unmask();
  },
  focusOut() {
    this._mask();
  },
  didInsertElement() {
    if (this.isSsn || this.isEin) {
      let formatter;
      if (this.isSsn) {
        formatter = new FieldKit.SocialSecurityNumberFormatter();
      } else {
        formatter = new FieldKit.EmployerIdentificationNumberFormatter();
      }
      this.set('fieldFormatter', formatter);
      this.set('field', new FieldKit.TextField(this.element.querySelector('input'), formatter));

      // FieldKit workaround, see https://github.com/square/field-kit/issues/62
      if (document.activeElement === this.field.element) {
        document.activeElement.blur();
      }
    }
  },
  // Internal value of the input field.
  _internalValue: null,
  // Internal flag tracking when the input has been changed.
  _isTouched: false,
  // For storing the unmasked value when `_internalValue` is masked. It is set
  // to a blank string for the initial unmask.
  _unmaskedValue: '',
  _unmask() {
    let unmaskedValue = this._unmaskedValue;

    // Normalize & force a reformat to ensure the unmasked value is display
    // ready.
    unmaskedValue = this._format(normalizeTin(unmaskedValue));
    this.set('_internalValue', unmaskedValue);
  },
  _mask() {
    const internalValue = this._internalValue;
    this.set('_unmaskedValue', internalValue);
    if (!this._isTouched && this.prefilledLast4) {
      this.set('_internalValue', maskTinLast4(this.prefilledLast4, this.isSsn, this.isEin));
    } else {
      this.set('_internalValue', maskTin(internalValue, this.isSsn, this.isEin));
    }
  },
  _format(string) {
    if (this.fieldFormatter) {
      return this.fieldFormatter.format(string);
    } else {
      return string;
    }
  },
  _unsetInvalidLast4() {
    const last4 = this.prefilledLast4;
    if (!isPresent(last4) || !TIN_LAST_FOUR_REGEX.test(last4)) {
      this.set('prefilledLast4', null);
    }
  },
  actions: {
    _onChange(event) {
      const value = event.target.value;

      // Update our internal representation for masking/unmasking.
      this.set('_internalValue', value);
      this.set('_isTouched', true);
      this.onChange(normalizeTin(value));
    }
  }
});